/**
 * Date conversion utilities for handling ISO 8601 date strings and Date objects
 * Used throughout the application to convert between API string responses and TypeScript Date types
 */

/**
 * Two kinds of date values reach the UI, and they must be displayed differently:
 *
 * - Date-only (SQL `DATE`, e.g. FDA approval/marketing dates, `data_file_date`,
 *   LEI incorporation, facility QTA/audit dates): the API serializes these as
 *   UTC midnight (`1998-11-05T00:00:00.000Z`). Show the calendar date as-is,
 *   never converted to the viewer's time zone, never with a time.
 *   -> `formatDateOnly`
 * - Timestamps (created/modified, `*_at`, audit `changed_date`): stored in UTC,
 *   shown in the viewer's own time zone with a zone label.
 *   -> `formatTimestamp`
 */
const parseDisplayDate = (value: unknown): Date | null => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(String(value));
  return isNaN(date.getTime()) ? null : date;
};

/**
 * Format a date-only value as the raw calendar date (e.g. `Nov 5, 1998`).
 * Reads the UTC calendar day, so the result is identical in every time zone.
 * Returns '' for empty/invalid input.
 */
export const formatDateOnly = (value: unknown): string => {
  const date = parseDisplayDate(value);
  if (!date) return '';
  return date.toLocaleDateString('en-US', {
    timeZone: 'UTC',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Format a UTC timestamp in the viewer's time zone, with a zone label
 * (e.g. `Oct 27, 2026, 9:07 AM EDT`). Returns '' for empty/invalid input.
 */
export const formatTimestamp = (value: unknown): string => {
  const date = parseDisplayDate(value);
  if (!date) return '';
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Whole-day number (days since epoch) of a date-only value's calendar day.
 * Returns null for empty/invalid input. Compare against `todayDayNumber()`
 * to reason about date-only values without mixing UTC-midnight instants and
 * the viewer's local clock.
 */
export const toDayNumber = (value: unknown): number | null => {
  const date = parseDisplayDate(value);
  if (!date) return null;
  return Math.floor(date.getTime() / MS_PER_DAY);
};

/** Day number of the viewer's current local calendar day. */
export const todayDayNumber = (): number => {
  const now = new Date();
  return Math.floor(
    Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()) / MS_PER_DAY
  );
};

/** Viewer's local calendar day as `YYYY-MM-DD` (e.g. for export file names). */
export const todayLocalISODate = (): string => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
};

/**
 * Converts an ISO 8601 date string to a Date object
 * Returns null if the input is null, undefined, or invalid
 */
export const parseISODate = (
  dateString: string | null | undefined
): Date | null => {
  if (!dateString) return null;
  const date = new Date(dateString);
  return isNaN(date.getTime()) ? null : date;
};

/**
 * Converts a Date object to an ISO 8601 string
 * Returns null if the input is null or undefined
 */
export const formatDateToISO = (
  date: Date | null | undefined
): string | null => {
  if (!date) return null;
  return date.toISOString();
};

/**
 * Converts a Date object to YYYY-MM-DD format for HTML date inputs
 * Returns empty string if the input is null or undefined
 */
export const formatDateForInput = (
  date: Date | string | null | undefined
): string => {
  if (!date) return '';

  // Handle both Date objects and ISO strings
  const dateObj = date instanceof Date ? date : new Date(date);

  if (isNaN(dateObj.getTime())) return '';

  return dateObj.toISOString().split('T')[0];
};

/**
 * Converts YYYY-MM-DD string from HTML date input to Date object
 * Returns null if the input is empty or invalid
 */
export const parseDateFromInput = (
  dateString: string | null | undefined
): Date | null => {
  if (!dateString || dateString.trim() === '') return null;

  // Handle YYYY-MM-DD format by appending time for consistent parsing
  const fullDateString = dateString.includes('T')
    ? dateString
    : `${dateString}T00:00:00.000Z`;
  return parseISODate(fullDateString);
};

/**
 * Converts API response date fields from strings to Date objects
 * Handles nested objects and arrays
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- generic constraint must accept plain TS interfaces without index signatures (e.g. CreateManufacturerRequest); Record<string, unknown> rejects those at call sites due to TS's index-signature check
export const convertDateFields = <T extends Record<string, any>>(
  data: T,
  dateFields: (keyof T)[]
): T => {
  if (!data || typeof data !== 'object') return data;

  const converted = { ...data };

  for (const field of dateFields) {
    const value = converted[field];
    if (typeof value === 'string') {
      converted[field] = parseISODate(value) as T[keyof T];
    }
  }

  return converted;
};

/**
 * Converts Date objects back to ISO strings for API requests
 * Handles nested objects and arrays
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- generic constraint must accept plain TS interfaces without index signatures (e.g. CreateManufacturerRequest); Record<string, unknown> rejects those at call sites due to TS's index-signature check
export const convertDatesToStrings = <T extends Record<string, any>>(
  data: T,
  dateFields?: readonly string[] | (keyof T)[]
): T => {
  if (!data || typeof data !== 'object') return data;

  const converted = { ...data };

  // If no specific fields provided, auto-detect date fields
  const fieldsToCheck =
    dateFields || Object.keys(converted).filter(key => /_date$/.test(key));

  for (const field of fieldsToCheck) {
    const value = converted[field as keyof T];
    if (value && Object.prototype.toString.call(value) === '[object Date]') {
      converted[field as keyof T] = formatDateToISO(
        value as Date
      ) as T[keyof T];
    }
  }

  return converted;
};

/**
 * Date field names for each domain entity
 */
export const DATE_FIELDS = {
  facility: [
    'qta_expiration_date',
    'audit_date',
    'created_date',
    'modified_date',
  ] as const,

  grade: ['created_date', 'modified_date'] as const,

  manufacturer: ['created_date', 'modified_date'] as const,

  product: ['created_date', 'modified_date'] as const,

  therapeuticArea: ['created_date', 'modified_date'] as const,
} as const;

/**
 * Checks if a date is valid
 */
export const isValidDate = (
  date: Date | string | null | undefined
): boolean => {
  if (!date) return false;
  const dateObj = date instanceof Date ? date : new Date(date);
  return !isNaN(dateObj.getTime());
};
